import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articleOrder, articleImages } from "@/mocks/articles";

const qcKeys = [
  { key: "photos", icon: "ri-image-line" },
  { key: "inspection", icon: "ri-search-eye-line" },
  { key: "seller", icon: "ri-user-star-line" },
  { key: "descriptions", icon: "ri-file-text-line" },
  { key: "payment", icon: "ri-bank-card-line" },
  { key: "shipping", icon: "ri-truck-line" },
  { key: "refund", icon: "ri-refund-2-line" },
  { key: "personal", icon: "ri-lock-line" },
  { key: "scam", icon: "ri-alert-line" },
];

interface ArticleContent {
  category: string;
  title: string;
  subtitle: string;
  updated: string;
}

export default function QcGuides() {
  const { t } = useTranslation();
  const articles = t("articles", { returnObjects: true }) as unknown as Record<
    string,
    ArticleContent
  >;

  return (
    <>
      <section id="qc" className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("qc.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">{t("qc.body")}</p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {qcKeys.map((item) => (
              <div
                key={item.key}
                className="flex items-start gap-3 rounded-xl border border-background-200 bg-white p-4"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                  <i className={`${item.icon} text-base`}></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">
                    {t(`qc.items.${item.key}.title`)}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                    {t(`qc.items.${item.key}.text`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <a
              href="#qc"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-300 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
            >
              {t("qc.read")}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      <section id="guides" className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("guides.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">
              {t("guides.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articleOrder.map((slug) => {
              const article = articles[slug];
              const image = articleImages[slug];
              if (!article) return null;
              return (
                <Link
                  key={slug}
                  to={`/guide/${slug}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-white transition-colors hover:border-primary-300"
                >
                  <div className="h-40 w-full overflow-hidden">
                    <img
                      src={image}
                      alt={article.title}
                      title={`${t("brand")} ${article.title}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                      {article.category}
                    </span>
                    <h3 className="mt-3 text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {article.subtitle}
                    </p>
                    <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
                      <span>{article.updated}</span>
                      <span className="inline-flex items-center gap-1 font-semibold text-primary-700">
                        {t("cta.readGuide")}
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}